import { FileInfoDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useMemo } from 'react';
import { getDocumentTitle, useFileCallbacks } from './common';
import { DocumentLoader } from './DocumentLoader';
import { DocumentOptionButton } from './DocumentOptionButton';
import styles from '@lmt/unicorn-shared/src/app/components/patients/profile/cases/components/documents/DocumentItem.module.scss';
import { DocumentIcon } from './DocumentIcon';

interface Props {
  document: FileInfoDto;
  isReadOnly?: boolean;
}

export const DocumentItem: FC<Props> = (props) => {
  const i18next = useScopedTranslation('patients.documents');
  const { document: patientDocument, isReadOnly } = props;

  const { isLoading, onClick } = useFileCallbacks(patientDocument);

  const title = useMemo(
    () => getDocumentTitle(patientDocument.name),
    [patientDocument.name],
  );
  const wasSentViaHl7 = !!patientDocument.values?.['HL7WasSent'];
  const wasSentViaGDT = !!patientDocument.values?.['GDTWasSent'];
  const isOutdated = !!patientDocument.values?.['IsOutdated'];
  return (
    <div className={styles.container} data-test-id="document-item">
      <div title={title} className={styles.titleContainer} onClick={onClick}>
        {isLoading && <DocumentLoader />}
        <div>
          <DocumentIcon
            data-loading={isLoading}
            fileType={patientDocument.type}
            filename={patientDocument.name}
            className={styles.icon}
          />
        </div>
        {wasSentViaHl7 && (
          <div className={styles.hl7SentIcon} data-test-id="hl7-sent-icon">
            HL7
          </div>
        )}
        {wasSentViaGDT && (
          <div
            data-bottom={wasSentViaHl7}
            className={styles.gdtSentIcon}
            data-test-id="gdt-sent-icon"
          >
            GDT
          </div>
        )}
        <div>
          <div className={styles.title} data-outdated={isOutdated}>
            {title}
          </div>
          {isOutdated && (
            <div
              className={styles.outdatedLabel}
              title={i18next.t('outdated_document_tooltip')}
            >
              {i18next.t('outdated_document')}
            </div>
          )}
        </div>
      </div>
      {!isReadOnly && (
        <div className={styles.optionContainer}>
          <div className={styles.option}>
            <DocumentOptionButton document={patientDocument} />
          </div>
        </div>
      )}
    </div>
  );
};
